/* person-detail.jsx — Premium Person Detail Redesign for Nexus CRM
   Overrides OverviewTab for People records only.
   Companies and Deals drawer remain unchanged.
   Loaded after overlays.jsx — all overlays.jsx globals accessible. */

/* ══════════════════════════════════════════════════════════════
   PERSON CONTACT ROW — rendered in hero below pills
   ══════════════════════════════════════════════════════════════ */
const PersonContactRow = ({ record }) => {
  const rows = [
    record.email && { icon: 'Mail',   text: record.email },
    record.phone && { icon: 'Phone',  text: record.phone },
    record.city  && { icon: 'MapPin', text: record.city  },
    record.linkedin && { icon: 'Link', text: 'LinkedIn Profile' },
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className="nx-phero-identity-block">
      {rows.map(r => (
        <div key={r.text} className="nx-phero-identity-item" title={r.text}>
          <NexusIcon name={r.icon} size={12} />
          <span>{r.text}</span>
        </div>
      ))}
    </div>
  );
};
window.PersonContactRow = PersonContactRow;

/* CRM Summary Strip */
const PersonCRMSummary = ({ record }) => {
  const TONE = { New: 'gray', Engaged: 'blue', Qualified: 'amber', Customer: 'green' };
  return (
    <div className="nx-phero-crm-strip">
      <div className="nx-pcs-cell">
        <Pill tone={TONE[record.status] || 'gray'}>{record.status}</Pill>
      </div>
      <div className="nx-pcs-div" />
      <div className="nx-pcs-cell">
        <NexusIcon name="TrendingUp" size={11} />
        <span className="nx-pcs-val">{record.score}</span>
        <span className="nx-pcs-lbl">/100</span>
      </div>
      <div className="nx-pcs-div" />
      <div className="nx-pcs-cell">
        <NexusIcon name="User" size={11} />
        <span className="nx-pcs-val">{(record.owner || '').split(' ')[0]}</span>
      </div>
      <div className="nx-pcs-div" />
      <div className="nx-pcs-cell">
        <NexusIcon name="Clock" size={11} />
        <span className="nx-pcs-val">{record.lastActivity || '—'}</span>
      </div>
    </div>
  );
};
window.PersonCRMSummary = PersonCRMSummary;

/* ══════════════════════════════════════════════════════════════
   AI INSIGHTS CARD
   ══════════════════════════════════════════════════════════════ */
const AIInsightsCard = ({ record, store }) => {
  const score = record.score || 0;
  const sentiment     = score >= 80 ? 'Positive' : score >= 50 ? 'Neutral' : 'At Risk';
  const sentimentTone = score >= 80 ? 'green'    : score >= 50 ? 'amber'   : 'red';
  const scoreColor    = score >= 80 ? 'var(--nx-success)' : score >= 50 ? '#F59E0B' : 'var(--nx-danger)';
  const openDeals     = store.deals.filter(d => d.contactId === record.id).length;
  const nextAction = score >= 80
    ? 'Schedule a proposal call — engagement signals are strong.'
    : score >= 50
    ? 'Send a relevant case study to re-engage.'
    : 'Personalise outreach — low engagement detected.';

  return (
    <div className="nx-ai-compact">
      <div className="nx-ai-compact-head">
        <NexusIcon name="Sparkles" size={11} />
        <span>AI Insights</span>
        <span className="nx-ai-compact-badge">{(store.branding?.brandName || 'AI') + ' AI'}</span>
      </div>
      <div className="nx-ai-compact-row">
        <div className="nx-ai-compact-cell">
          <span className="nx-ai-compact-num" style={{ color: scoreColor }}>{score}</span>
          <div className="nx-ai-compact-bar"><div style={{ width: score+'%', background: scoreColor }} /></div>
          <span className="nx-ai-compact-lbl">Score</span>
        </div>
        <div className="nx-ai-compact-cell">
          <Pill tone={sentimentTone}>{sentiment}</Pill>
          <span className="nx-ai-compact-lbl">Sentiment</span>
        </div>
        <div className="nx-ai-compact-cell">
          <span className="nx-ai-compact-val">{record.lastActivity || '—'}</span>
          <span className="nx-ai-compact-lbl">Last Contact</span>
        </div>
        {openDeals > 0 && (
          <div className="nx-ai-compact-cell">
            <span className="nx-ai-compact-val" style={{ color:'var(--nx-accent)', fontSize:16, fontWeight:800 }}>{openDeals}</span>
            <span className="nx-ai-compact-lbl">Open Deals</span>
          </div>
        )}
      </div>
      <div className="nx-ai-compact-nba">
        <NexusIcon name="Zap" size={10} />
        <span>{nextAction}</span>
      </div>
    </div>
  );
};
;

/* ══════════════════════════════════════════════════════════════
   ENHANCED ACTIVITY TIMELINE — date-grouped icon cards
   ══════════════════════════════════════════════════════════════ */
const PersonTimeline = ({ items = [] }) => {
  const ACT_ICON = {
    whatsapp: 'MessageCircle', deal: 'Target', email: 'Mail',
    note: 'StickyNote', call: 'Phone', meeting: 'Calendar',
    followup: 'Clock', reminder: 'Bell', activity: 'Activity',
  };
  const ACT_BG = {
    whatsapp: '#E7F5EE', deal: '#EDF4FD', email: '#F3EDFB',
    note: '#FEF6E6', call: '#E7F5EE', meeting: '#EDF4FD',
    followup: '#FEF6E6',
  };
  const ACT_FG = {
    whatsapp: '#0E8C5A', deal: '#125AB0', email: '#6E3FBA',
    note: '#B27800', call: '#0E8C5A', meeting: '#125AB0',
    followup: '#B27800',
  };

  const groupKey = (time) => {
    const t = (time || '').toLowerCase();
    if (t.includes('just') || t.includes('1m') || t.includes('2m')) return 'Just Now';
    if (t.includes('min') || t.includes('hr') || t.match(/^\d+h/)) return 'Today';
    if (t.includes('1d') || t.includes('yesterday')) return 'Yesterday';
    if (t.match(/[2-6]d/)) return 'This Week';
    return 'Earlier';
  };

  const grouped = React.useMemo(() => {
    const map = new Map();
    const ORDER = ['Just Now', 'Today', 'Yesterday', 'This Week', 'Earlier'];
    items.forEach(a => {
      const k = groupKey(a.time);
      const list = map.get(k) || [];
      list.push(a);
      map.set(k, list);
    });
    return ORDER.filter(k => map.has(k)).map(k => [k, map.get(k)]);
  }, [items]);

  if (!items.length) return (
    <div className="nx-empty" style={{ padding: 'var(--space-5) 0' }}>
      <NexusIcon name="Activity" size={20} />
      <span>No activity recorded yet.</span>
    </div>
  );

  return (
    <div className="nx-ptl">
      {grouped.map(([date, acts]) => (
        <div key={date} className="nx-ptl-group">
          <div className="nx-ptl-date">{date}</div>
          {acts.map(a => {
            const bg = ACT_BG[a.type] || 'var(--nx-surface-alt)';
            const fg = ACT_FG[a.type] || 'var(--nx-text-muted)';
            const ic = ACT_ICON[a.type] || 'Activity';
            return (
              <div key={a.id} className="nx-ptl-card">
                <div className="nx-ptl-icon" style={{ background: bg, color: fg }}>
                  <NexusIcon name={ic} size={12} />
                </div>
                <div className="nx-ptl-body">
                  <div className="nx-ptl-primary">
                    <strong>{a.who}</strong> {a.what}
                  </div>
                  {a.detail && <div className="nx-ptl-detail">{a.detail}</div>}
                  <div className="nx-ptl-time">{a.time}</div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   RELATED RECORD ROW
   ══════════════════════════════════════════════════════════════ */
const PersonRelatedRow = ({ iconName, iconBg, iconColor, title, sub, onClick }) => (
  <button className="nx-prelated-row" onClick={onClick}>
    <span className="nx-prelated-icon" style={{ background: iconBg, color: iconColor }}>
      <NexusIcon name={iconName} size={12} />
    </span>
    <div className="nx-prelated-body">
      <span className="nx-prelated-title">{title}</span>
      {sub && <span className="nx-prelated-sub">{sub}</span>}
    </div>
    <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
  </button>
);

/* ══════════════════════════════════════════════════════════════
   OPPORTUNITIES TAB — linked deals
   ══════════════════════════════════════════════════════════════ */
const PersonOpportunitiesTab = ({ record, store }) => {
  const stages = store.pipeline || [];
  const firstName = (record.name || '').split(' ')[0];
  const deals = store.deals.filter(d => d.contactId === record.id);
  const fmt = n => '$' + (n >= 1000 ? (n/1000).toFixed(1) + 'K' : n);
  const totalVal = deals.reduce((s, d) => s + d.value, 0);

  return (
    <div className="nx-drawer-page">
      <div className="nx-popp-toolbar">
        {deals.length > 0 && (
          <div className="nx-popp-summary">
            <span className="nx-popp-total">{fmt(totalVal)}</span>
            <span className="nx-muted nx-mini">across {deals.length} deal{deals.length !== 1 ? 's' : ''}</span>
          </div>
        )}
        <Btn variant="primary" size="sm" icon="Plus"
          onClick={() => store.openCreateForm('deals', { contactId: record.id, companyId: record.companyId || null, owner: record.owner || '', title: 'Deal with ' + firstName, _context: { source: 'person', readOnly: ['contactId', 'companyId', 'owner'] } })}>
          New Deal
        </Btn>
      </div>

      {deals.length === 0 ? (
        <div className="nx-empty" style={{ flexDirection:'column', padding:'var(--space-12) 0', gap:'var(--space-3)' }}>
          <NexusIcon name="TrendingUp" size={28} />
          <span>No deals linked to {firstName} yet.</span>
          <Btn variant="secondary" size="sm" icon="Plus"
            onClick={() => store.openCreateForm('deals', { contactId: record.id, companyId: record.companyId || null, owner: record.owner || '', _context: { source: 'person', readOnly: ['contactId', 'companyId', 'owner'] } })}>
            Create First Deal
          </Btn>
        </div>
      ) : (
        <div className="nx-popp-list">
          {deals.sort((a, b) => b.value - a.value).map(d => {
            const stage = stages.find(s => s.id === d.stageId);
            const co = store.companies.find(c => c.id === d.companyId);
            const prob = d.probability ?? stage?.prob ?? 0;
            let stagePillTone = 'blue';
            if (stage?.kind === 'WON') { stagePillTone = 'green'; }
            else if (stage?.kind === 'LOST') { stagePillTone = 'red'; }
            return (
              <button key={d.id} className="nx-popp-card" onClick={() => store.openDrawer(d)}>
                <div className="nx-popp-card-head">
                  <span className="nx-popp-card-title">{d.title}</span>
                  <span className="nx-popp-card-val">{fmt(d.value)}</span>
                </div>
                <div className="nx-popp-card-meta">
                  {stage && <Pill tone={stagePillTone}>{stage.name}</Pill>}
                  {co    && <span className="nx-muted nx-mini">{co.name}</span>}
                  <span className="nx-muted nx-mini" style={{ marginLeft:'auto' }}>{d.closeDate}</span>
                </div>
                <div className="nx-popp-bar-wrap">
                  <div className="nx-popp-bar"><div style={{ width: prob + '%', background: stage?.tone || 'var(--nx-accent)' }} /></div>
                  <span className="nx-popp-bar-pct">{prob}%</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
window.PersonOpportunitiesTab = PersonOpportunitiesTab;

/* ══════════════════════════════════════════════════════════════
   PERSON OVERVIEW TAB — premium reorganized layout
   ══════════════════════════════════════════════════════════════ */
const PersonOverviewTab = ({ record, type, setTab }) => {
  const store = useStore();
  const _stages = store.pipeline || [];
  const update = (field, value) => store.update(type, record.id, { [field]: value });

  const linkedCompany = store.companies.find(c => c.id === record.companyId);
  const openDeals     = store.deals.filter(d => d.contactId === record.id);
  const openTasks = store.tasks.filter(t => !t.done && t.personId === record.id).slice(0, 4);

  return (
    <div className="nx-drawer-overview" style={{ padding: 0 }}>
      <div className="nx-pov-layout">

        {/* ── MAIN column: AI + Professional + Activity preview ── */}
        <div className="nx-pov-main">

          <AIInsightsCard record={record} store={store} />

          <DrawerPanel title="Professional Information">
            <div className="nx-prop-grid nx-profinfo-grid">
              <SelectProp label="Salutation" value={record.salutation || ''}
                options={[{ value: '', label: '—' }, 'Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.']}
                onSave={v => update('salutation', v || null)} />
              <EditableProp label="Role"       value={record.role}         onSave={v => update('role', v)} />
              <div className="nx-prop">
                <span className="nx-prop-label">Company</span>
                <CompanyPicker
                  value={record.companyId}
                  onChange={id => {
                    const c = store.companies.find(x => x.id === id);
                    store.update(type, record.id, { companyId: id || null, company: c?.name || '' });
                  }}
                />
              </div>
              <div className="nx-prop">
                <span className="nx-prop-label">Industry</span>
                <span className="nx-prop-value" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{linkedCompany ? (linkedCompany.industry || '—') : '—'}</span>
                  {linkedCompany ? (
                    <span className="nx-muted nx-mini" style={{ fontWeight: 400, display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden', minWidth: 0, maxWidth: '100%' }}>
                      <span style={{ flexShrink: 0 }}>From</span>
                      <button
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--nx-text-muted)', font: 'inherit', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1 }}
                        onClick={() => { window.__nx_drawer_back = record; store.openDrawer(linkedCompany); }}
                      >{linkedCompany.name}</button>
                    </span>
                  ) : (
                    <span className="nx-muted nx-mini" style={{ fontWeight: 400 }}>Set a company to see industry</span>
                  )}
                </span>
              </div>
              <EditableProp label="Email"      value={record.email}   mono onSave={v => update('email', v)} />
              <EditableProp label="Phone"      value={record.phone}   mono onSave={v => update('phone', v)} />
            </div>
          </DrawerPanel>

          {/* Custom Fields — CF-05: People OverviewTab integration (CF-00 P.3) */}
          <DrawerPanel title="Custom Fields">
            {window.CustomFieldsPanel
              ? React.createElement(window.CustomFieldsPanel, { record, entityType: 'people' })
              : <span className="nx-muted nx-mini" style={{ padding: '8px 0', display: 'block' }}>Custom fields not available.</span>
            }
          </DrawerPanel>

          {/* Activity preview — 3 items only; full history in Activity tab */}
          <DrawerPanel title="Recent Activity"
            actions={<button className="nx-panel-link" onClick={() => setTab && setTab('activity')}>See All <NexusIcon name="ArrowRight" size={11} /></button>}>
            <PersonTimeline items={store.activity.filter(a => a.personId === record.id).slice(0, 3)} />
          </DrawerPanel>

        </div>

        {/* ── SIDE column: CRM + Tags + Related ── */}
        <div className="nx-pov-side">

          <DrawerPanel title="CRM Snapshot">
            <div className="nx-crm-snapshot">
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Status</span>
                <SelectProp label="" value={record.status}
                  options={['New','Engaged','Qualified','Customer']}
                  onSave={v => update('status', v)} pill="blue" />
              </div>
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Owner</span>
                <SelectProp label="" value={record.owner}
                  options={store.memberNames?.length ? store.memberNames : [window.__nx_auth?.user?.name].filter(Boolean)}
                  onSave={v => update('owner', v)} avatar />
              </div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Score</span><Score value={record.score} /></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Last Activity</span><span className="nx-crm-val">{record.lastActivity || '—'}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">City</span><span className="nx-crm-val">{record.city || '—'}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Created</span><span className="nx-crm-val">{record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
            </div>
          </DrawerPanel>

          <DrawerPanel title="Tags">
            <div className="nx-prop-grid">
              <TagEditor record={record} type="people" />
            </div>
          </DrawerPanel>

          {typeof BFSIPersonProps !== 'undefined' && BFSIPersonProps &&
            (store.modules || []).some(m => /bfsi/i.test(m)) && (
            <DrawerPanel title="BFSI Profile"
              actions={<Pill tone={record.riskLevel === 'Low' ? 'green' : record.riskLevel === 'High' ? 'red' : 'amber'}>{record.riskLevel || '—'}</Pill>}>
              <BFSIPersonProps record={record} />
            </DrawerPanel>
          )}

          <DrawerPanel title="Related Records">
            {/* Company */}
            {(linkedCompany || record.company) && (
              <div className="nx-prelated-section">
                <div className="nx-prelated-label">Company</div>
                {linkedCompany ? (
                  <PersonRelatedRow
                    iconName="Building2"
                    iconBg="var(--nx-accent-tint)" iconColor="var(--nx-accent)"
                    title={linkedCompany.name}
                    sub={linkedCompany.industry + ' · ' + linkedCompany.location}
                    onClick={() => {
                      window.__nx_drawer_back = record;
                      store.openDrawer(linkedCompany);
                    }}
                  />
                ) : (
                  <div className="nx-muted nx-mini" style={{ padding: '4px 0' }}>{record.company}</div>
                )}
              </div>
            )}

            {/* Deals */}
            <div className="nx-prelated-section">
              <div className="nx-prelated-label">
                Deals
                <div style={{ marginLeft:'auto', display:'flex', gap: 'var(--space-2)' }}>
                  {openDeals.length > 0 && (
                    <button className="nx-panel-link" onClick={() => setTab && setTab('opportunities')}>
                      See all <NexusIcon name="ArrowRight" size={10} />
                    </button>
                  )}
                  <button className="nx-panel-link" onClick={() => store.openCreateForm('deals', { contactId: record.id, companyId: record.companyId || null, owner: record.owner || '', _context: { source: 'person', readOnly: ['contactId', 'companyId', 'owner'] } })}>
                    <NexusIcon name="Plus" size={10} /> Add
                  </button>
                </div>
              </div>
              {openDeals.slice(0, 2).map(d => {
                const stage = _stages.find(s => s.id === d.stageId);
                const fmt   = n => '$' + (n >= 1000 ? (n/1000).toFixed(1)+'K' : n);
                return (
                  <PersonRelatedRow key={d.id}
                    iconName="TrendingUp"
                    iconBg="var(--nx-accent-tint)" iconColor="var(--nx-accent)"
                    title={d.title}
                    sub={(stage?.name || '') + ' · ' + fmt(d.value)}
                    onClick={() => { window.__nx_drawer_back = record; store.openDrawer(d); }}
                  />
                );
              })}
              {openDeals.length === 0 && (
                <div className="nx-muted nx-mini" style={{ padding:'4px 0', display:'block' }}>No deals yet.</div>
              )}
            </div>

            {/* Open Tasks — FIX: switch to Tasks tab, keep context */}
            <div className="nx-prelated-section">
              <div className="nx-prelated-label">
                Open Tasks
                <button type="button" className="nx-panel-link" style={{ marginLeft:'auto' }}
                  onClick={() => setTab && setTab('tasks')}>
                  <NexusIcon name="Plus" size={10} /> Add
                </button>
              </div>
              {openTasks.map(t => (
                <div key={t.id} className={'nx-mini-task' + (t.done ? ' is-done' : '')}>
                  <button type="button" className="nx-check"
                    onClick={e => { e.stopPropagation(); store.update('tasks', t.id, { done: !t.done }); }}>
                    {t.done && <NexusIcon name="Check" size={10} />}
                  </button>
                  <span className="nx-task-title" style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</span>
                  <Pill tone={t.priority==='High'?'red':t.priority==='Medium'?'amber':'gray'}>{t.priority}</Pill>
                </div>
              ))}
              {openTasks.length === 0 && (
                <div className="nx-muted nx-mini" style={{ padding:'4px 0', display:'block' }}>No open tasks.</div>
              )}
            </div>
          </DrawerPanel>

          <DrawerPanel title="Record Metadata">
            <Prop label="Record ID" value={record.id} mono />
            <Prop label="Created"  value={record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <Prop label="Modified" value={record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <Prop label="Source"   value={record.sourceSystem || 'Manual'} />
          </DrawerPanel>

        </div>{/* end nx-pov-side */}
      </div>{/* end nx-pov-layout */}
    </div>
  )
};
window.PersonOverviewTab = PersonOverviewTab;
